<template>
  <!-- Interruttore giorno/notte della barra globale (Angelo, 4/11/2026): un cielo di vetro che si scurisce, con un sole terracotta che diventa luna d'avorio, nuvole
       che scendono e stelle che si accendono. Niente bordo: il fondo stesso fa da contorno. Si muovono solo `transform` e `opacity` (compositore), tutti con la
       stessa durata e la stessa curva, così sole, cielo, nuvole e stelle partono e arrivano insieme. `view-transition-name`: durante l'onda del cambio tema
       l'interruttore resta vivo sopra l'immagine ferma della pagina (regole in `base/base.css`). -->
  <button
    type="button"
    role="switch"
    :aria-checked="scuro ? 'true' : 'false'"
    :aria-label="ariaLabel"
    class="tema-switch focusable relative block h-8 w-16 shrink-0 cursor-pointer overflow-hidden rounded-full"
    :class="scuro && 'tema-switch-scuro'"
    style="view-transition-name: tema-switch"
    data-tema-switch
    @click="scuro = !scuro"
  >
    <span class="tema-cielo tema-cielo-giorno" aria-hidden="true" />
    <span class="tema-cielo tema-cielo-notte" aria-hidden="true" />
    <span class="tema-stelle" aria-hidden="true"><i v-for="n in 5" :key="n" :class="`tema-stella tema-stella-${n}`" /></span>
    <span class="tema-nuvole" aria-hidden="true"><i class="tema-nuvola tema-nuvola-1" /><i class="tema-nuvola tema-nuvola-2" /></span>
    <span class="tema-pomo" aria-hidden="true">
      <span class="tema-sole" />
      <span class="tema-luna"><i class="tema-cratere tema-cratere-1" /><i class="tema-cratere tema-cratere-2" /><i class="tema-cratere tema-cratere-3" /></span>
    </span>
  </button>
</template>

<script setup lang="ts">
// `true` = tema scuro. Il clic cambia il valore; chi usa il componente decide come (nella barra: l'onda di `useCambioTema`).
defineProps<{ ariaLabel: string }>();
const scuro = defineModel<boolean>({ default: false });
</script>

<style scoped>
.tema-switch {
  --durata: var(--motion-fluid, 550ms);
  --curva: var(--ease-fluid, cubic-bezier(0.65, 0, 0.35, 1));
  --corsa: 2rem; /* larghezza 4rem − pomo 1.5rem − due margini da 0.25rem */
  box-shadow: inset 0 1px 3px rgb(0 0 0 / 0.28), 0 0 0 1px color-mix(in srgb, var(--color-graphite) 12%, transparent);
  isolation: isolate;
}

/* Due cieli sovrapposti: un gradiente non si anima, ma l'opacità sì */
.tema-cielo {
  position: absolute;
  inset: 0;
  transition: opacity var(--durata) var(--curva);
}
.tema-cielo-giorno {
  background: linear-gradient(180deg, color-mix(in srgb, var(--color-cool-steel) 70%, var(--color-ivory)) 0%, var(--color-ivory) 100%);
}
.tema-cielo-notte {
  background: linear-gradient(180deg, #1e1b18 0%, var(--color-graphite) 100%);
  opacity: 0;
}
.tema-switch-scuro .tema-cielo-notte {
  opacity: 1;
}

/* Sole e luna: lo stesso pomo che scorre da un lato all'altro; il sole si dissolve ruotando mentre la luna compare */
.tema-pomo {
  position: absolute;
  top: 0.25rem;
  left: 0.25rem;
  width: 1.5rem;
  height: 1.5rem;
  transition: transform var(--durata) var(--curva);
  will-change: transform;
}
.tema-switch-scuro .tema-pomo {
  transform: translateX(var(--corsa));
}
.tema-switch:hover .tema-pomo {
  scale: 1.06;
}
.tema-sole,
.tema-luna {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  transition:
    opacity var(--durata) var(--curva),
    transform var(--durata) var(--curva);
}
.tema-sole {
  background: var(--color-fiery-terracotta);
  box-shadow:
    0 0 0 0.2rem color-mix(in srgb, var(--color-fiery-terracotta) 22%, transparent),
    0 0 0.7rem color-mix(in srgb, var(--color-fiery-terracotta) 55%, transparent);
}
.tema-luna {
  background: var(--color-ivory-2);
  box-shadow: 0 0 0.6rem color-mix(in srgb, var(--color-ivory-2) 40%, transparent);
  opacity: 0;
  transform: rotate(-60deg) scale(0.7);
}
.tema-switch-scuro .tema-sole {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}
.tema-switch-scuro .tema-luna {
  opacity: 1;
  transform: none;
}
.tema-cratere {
  position: absolute;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--color-cool-steel) 55%, var(--color-ivory-2));
}
.tema-cratere-1 { top: 22%; left: 52%; width: 28%; height: 28%; }
.tema-cratere-2 { top: 58%; left: 24%; width: 22%; height: 22%; }
.tema-cratere-3 { top: 62%; left: 62%; width: 14%; height: 14%; }

/* Nuvole: ciuffi d'avorio in basso, scendono e spariscono di notte */
.tema-nuvole,
.tema-stelle {
  position: absolute;
  inset: 0;
  transition:
    transform var(--durata) var(--curva),
    opacity var(--durata) var(--curva);
}
.tema-nuvole {
  pointer-events: none;
}
.tema-switch-scuro .tema-nuvole {
  transform: translateY(70%);
  opacity: 0;
}
.tema-nuvola {
  position: absolute;
  border-radius: 9999px;
  background: var(--color-ivory-2);
  opacity: 0.92;
}
.tema-nuvola-1 { bottom: -0.3rem; right: 0.25rem; width: 1.5rem; height: 0.85rem; }
.tema-nuvola-2 { bottom: -0.35rem; right: 1.4rem; width: 1.1rem; height: 0.7rem; opacity: 0.75; }

/* Stelle: puntini d'avorio che si accendono di notte, piccoli e a quote diverse */
.tema-stelle {
  opacity: 0;
  transform: translateY(-40%);
}
.tema-switch-scuro .tema-stelle {
  opacity: 1;
  transform: none;
}
.tema-stella {
  position: absolute;
  border-radius: 9999px;
  background: var(--color-ivory-2);
}
.tema-stella-1 { top: 22%; left: 20%; width: 0.18rem; height: 0.18rem; }
.tema-stella-2 { top: 58%; left: 34%; width: 0.12rem; height: 0.12rem; opacity: 0.8; }
.tema-stella-3 { top: 30%; left: 44%; width: 0.12rem; height: 0.12rem; opacity: 0.7; }
.tema-stella-4 { top: 70%; left: 14%; width: 0.15rem; height: 0.15rem; opacity: 0.85; }
.tema-stella-5 { top: 16%; left: 32%; width: 0.1rem; height: 0.1rem; opacity: 0.6; }

@media (prefers-reduced-motion: reduce) {
  .tema-switch :is(.tema-cielo, .tema-pomo, .tema-sole, .tema-luna, .tema-nuvole, .tema-stelle) {
    transition: none;
  }
}
</style>
